import React, { useEffect, useMemo, useState } from 'react';
import MemberService from '../../services/MemberService';
import RoleService from '../../services/RoleService';
import utility from '../../utils/utility';
import ReactPaginate from 'react-paginate';
import FilterBar from '../../components/FilterBar';
import { filterList } from '../../utils/filterUtils';

const Member = () => {
    const [members, setMembers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [pageCount, setPageCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 20;
    const [searchText, setSearchText] = useState('');
    const [formData, setFormData] = useState({
        id: '',
        memberName: '',
        roleId: '',
        youtubeLink: '',
        instaLink: '',
        facebookLink: '',
        website: '',
        status: 'Active',
        isActive: 1
    });
    const filteredMembers = useMemo(() => filterList(members, searchText, ['memberName', 'roleName', 'status', 'id']), [members, searchText]);

    useEffect(() => {
        fetchDropdownData();
        fetchData();
    }, []);

    useEffect(() => {
        fetchData();
    }, [currentPage]);

    const fetchDropdownData = async () => {
        try {
            const rolesRes = await RoleService.getAll(1, 100);
            setRoles(rolesRes.data || []);
        } catch (err) {
            utility.showError('Failed to load roles: ' + err.message);
        }
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            const data = await MemberService.getAll(currentPage, itemsPerPage);
            setMembers(data.data || []);
            setPageCount(data.pagination.totalPages || 0);
        } catch (err) {
            utility.showError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (member = null) => {
        if (member) {
            setEditMode(true);
            setFormData({
                id: member.id,
                memberName: member.memberName,
                roleId: member.roleId || '',
                youtubeLink: member.youtubeLink || '',
                instaLink: member.instaLink || '',
                facebookLink: member.facebookLink || '',
                website: member.website || '',
                status: member.status || 'Active',
                isActive: member.isActive
            });
        } else {
            setEditMode(false);
            setFormData({
                id: '',
                memberName: '',
                roleId: '',
                youtubeLink: '',
                instaLink: '',
                facebookLink: '',
                website: '',
                status: 'Active',
                isActive: 1
            });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: (name === 'isActive') ? parseInt(value) : value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            if (editMode) {
                await MemberService.update(formData.id, formData);
                utility.showSuccess('Member updated successfully');
            } else {
                await MemberService.create(formData);
                utility.showSuccess('Member created successfully');
            }
            handleCloseModal();
            fetchData();
        } catch (err) {
            utility.showError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleToggleStatus = async (member) => {
        try {
            const newStatus = member.isActive ? 0 : 1;
            await MemberService.updateStatus(member.id, newStatus);
            utility.showSuccess(`Member status ${newStatus ? 'activated' : 'deactivated'} successfully`);
            fetchData();
        } catch (err) {
            utility.showError(err.message);
        }
    };

    const handlePageClick = (event) => {
        const selectedPage = event.selected + 1;
        setCurrentPage(selectedPage);
    };

    return (
        <div className="container-fluid py-2">
            <div className="row">
                <div className="col-12 text-end mb-3">
                    <button className="btn bg-gradient-dark mb-0" onClick={() => handleOpenModal()}>
                        <i className="material-symbols-rounded text-sm">add</i>&nbsp;&nbsp;Add New Member
                    </button>
                </div>
                <div className="col-12">
                    <div className="card my-4">
                        <div className="card-header p-0 position-relative mt-n4 mx-3 z-index-2">
                            <div className="bg-gradient-dark shadow-dark border-radius-lg pt-4 pb-3">
                                <h6 className="text-white text-capitalize ps-3">Members</h6>
                            </div>
                        </div>
                        <div className="card-body px-0 pb-2">
                            {/* Error alert removed in favor of professional toasts */}
                            <div className="px-3">
                                <FilterBar
                                    value={searchText}
                                    onChange={setSearchText}
                                    onClear={() => setSearchText('')}
                                    placeholder="Search members..."
                                />
                            </div>
                            <div className="table-responsive p-0">
                                {loading ? (
                                    <div className="p-4 text-center">Loading...</div>
                                ) : (
                                    <table className="table align-items-center mb-0">
                                        <thead>
                                            <tr>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Member Name</th>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Role</th>
                                                <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Status</th>
                                                <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredMembers.length === 0 ? (
                                                <tr><td colSpan="4" className="text-center py-4">No members found.</td></tr>
                                            ) : (
                                                filteredMembers.map((member) => (
                                                    <tr key={member.id}>
                                                        <td>
                                                            <div className="d-flex px-2 py-1">
                                                                <div className="d-flex flex-column justify-content-center">
                                                                    <h6 className="mb-0 text-sm">{member.memberName}</h6>
                                                                    <p className="text-xs text-secondary mb-0">{member.website || 'No Website'}</p>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <p className="text-xs font-weight-bold mb-0">{member.roleName || member.role || 'General'}</p>
                                                        </td>
                                                        <td className="align-middle text-center text-sm">
                                                            <span
                                                                className={`badge badge-sm bg-gradient-${member.isActive ? 'success' : 'secondary'} cursor-pointer`}
                                                                onClick={() => handleToggleStatus(member)}
                                                            >
                                                                {member.isActive ? 'Active' : 'Inactive'}
                                                            </span>
                                                        </td>
                                                        <td className="align-middle text-center">
                                                            <button className="btn btn-link text-info mb-0" onClick={() => handleOpenModal(member)}>
                                                                Edit
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <td colSpan="4">
                                                    <ReactPaginate
                                                        forcePage={currentPage - 1}
                                                        breakLabel="..."
                                                        nextLabel=">"
                                                        onPageChange={handlePageClick}
                                                        pageRangeDisplayed={3}
                                                        marginPagesDisplayed={2}
                                                        pageCount={pageCount}
                                                        previousLabel="<"
                                                        containerClassName="pagination"
                                                        pageClassName="page-item"
                                                        pageLinkClassName="page-link"
                                                        previousClassName="page-item"
                                                        previousLinkClassName="page-link"
                                                        nextClassName="page-item"
                                                        nextLinkClassName="page-link"
                                                        activeClassName="active"
                                                        disabledClassName="disabled"
                                                        breakClassName="page-item"
                                                        breakLinkClassName="page-link"
                                                    />
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content">
                            <div className="modal-header border-0">
                                <h5 className="modal-title font-weight-normal">{editMode ? 'Edit Member' : 'Add New Member'}</h5>
                                <button type="button" className="btn-close text-dark" onClick={handleCloseModal}>
                                    <span aria-hidden="true">&times;</span>
                                </button>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <div className="modal-body row">
                                    <div className="col-md-6 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Member Name</label>
                                            <input type="text" name="memberName" className="form-control" value={formData.memberName} onChange={handleInputChange} required />
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Role</label>
                                            <select
                                                name="roleId"
                                                className="form-control"
                                                value={formData.roleId}
                                                onChange={handleInputChange}
                                            >
                                                <option value="">Select Role</option>
                                                {roles.map(role => (
                                                    <option key={role.id} value={role.id}>
                                                        {role.roleName}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Website</label>
                                            <input type="text" name="website" className="form-control" value={formData.website} onChange={handleInputChange} />
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>YouTube Link</label>
                                            <input type="text" name="youtubeLink" className="form-control" value={formData.youtubeLink} onChange={handleInputChange} />
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Instagram Link</label>
                                            <input type="text" name="instaLink" className="form-control" value={formData.instaLink} onChange={handleInputChange} />
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Facebook Link</label>
                                            <input type="text" name="facebookLink" className="form-control" value={formData.facebookLink} onChange={handleInputChange} />
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Status Label</label>
                                            <input type="text" name="status" className="form-control" value={formData.status} onChange={handleInputChange} />
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Is Active</label>
                                            <select name="isActive" className="form-control" value={formData.isActive} onChange={handleInputChange}>
                                                <option value={1}>Active</option>
                                                <option value={0}>Inactive</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer border-0">
                                    <button type="button" className="btn bg-gradient-secondary" onClick={handleCloseModal} disabled={saving}>Cancel</button>
                                    <button type="submit" className="btn bg-gradient-dark" disabled={saving}>
                                        {saving ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                {editMode ? 'Updating...' : 'Saving...'}
                                            </>
                                        ) : (
                                            editMode ? 'Update' : 'Save'
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Member;
